/**
 * Bipartite Chart Component - Center Panel
 * Visualizes many-to-many relationships between tags and standards
 */

import { Box, Typography, Tooltip, styled, Alert, Button } from '@mui/material';
import Plot from 'react-plotly.js';
import { TagCandidate, Standard, CHART_COLORS, MathExpression } from '../../types';
import { useMemo, useRef, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { standardsApi, filterStandards } from '../../services/spine.service';
import { createStandardsKey, buildPreviewRequests, extractLimiterIds, extractSelectedMathExpressionIds } from '../../utils/standardsPreview';

// Helper function to wrap text at specified character length
const wrapText = (text: string, maxLength: number = 50): string => {
  if (text.length <= maxLength) return text;

  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    if ((currentLine + ' ' + word).length <= maxLength) {
      currentLine += currentLine ? ' ' + word : word;
    } else {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        // Word is longer than maxLength, break it
        lines.push(word.substring(0, maxLength - 3) + '...');
        currentLine = '';
      }
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines.join('<br>');
};

// Helper function to style tag tooltip text (splits on "/" and wraps each segment)
const styleTagTooltipText = (text: string, maxLength: number = 45): string => {
  // Split by "/" to get hierarchy segments
  const segments = text.split('/');

  // Wrap each segment individually and add bullet
  const wrappedSegments = segments.map(segment => {
    const trimmedSegment = segment.trim();
    if (!trimmedSegment) return ''; // Skip empty segments
    const wrappedText = wrapText(trimmedSegment, maxLength - 2); // Account for bullet space
    return '• ' + wrappedText;
  }).filter(segment => segment); // Remove empty segments

  // Join with line breaks to show hierarchy
  return wrappedSegments.join('<br>');
};

// Helper function to style standard tooltip text
const styleStandardTooltipText = (standard: Standard, maxLength: number = 45): string => {
  const stateText = standard.state || 'N/A';
  const subjectText = standard.subject || 'N/A';

  const header = `<b>${standard.code}</b><br>` +
    `• Grade: ${standard.grade}<br>` +
    `• Document: ${standard.document}<br>` +
    `• Jurisdiction: ${standard.jurisdiction}<br>` +
    `• State: ${stateText}<br>` +
    `• Subject: ${subjectText}<br><br>`;

  const wrappedDescription = wrapText(standard.description, maxLength);

  return header + wrappedDescription;
};

// Styled wrapper to customize Plotly hover labels
const StyledPlotWrapper = styled(Box)({
  '& .hoverlayer .hovertext': {
    maxWidth: '200px !important',
    whiteSpace: 'normal !important',
    wordWrap: 'break-word !important',
    lineHeight: '1.3 !important',
    padding: '8px 12px !important',
  }
});

interface BipartiteChartProps {
  tagCandidates: TagCandidate[];
  mathExpressions?: MathExpression[];
  focusedTagIndex: number | null;
  onTagFocus: (tagIndex: number | null) => void;
  selectedGrades: string[];
  selectedDocuments: string[];
  selectedJurisdictions: string[];
  selectedStates: string[];
  showNonSelectedTags: boolean;
  searchText: string;
  externalFocusedStandardId?: string | null;  // Standard selected externally (from Standards tab)
  onFocusedStandardsChange?: (focusedData: {
    focusedStandardId: string | null;     // directly clicked standard (green)
    focusedTagStandardIds: string[];     // standards from focused tag (yellow)
  }) => void;
  // Dirty state props
  isDirty?: boolean;
  onRevertChanges?: () => void;
}

// Color palette - using shared constants
const COLORS = {
  SELECTED: CHART_COLORS.ALIGNED,
  UNSELECTED: CHART_COLORS.NOT_ALIGNED,
  FOCUSED: CHART_COLORS.FOCUSED_TAG,
  REVERSE_FOCUSED: CHART_COLORS.FOCUSED_STANDARD,
  BORDER: CHART_COLORS.BORDER,
};

// Maximum number of standard nodes to render for performance
const MAX_STANDARD_NODES = 500;

export default function BipartiteChart({
  tagCandidates,
  mathExpressions,
  focusedTagIndex,
  onTagFocus,
  selectedGrades,
  selectedDocuments,
  selectedJurisdictions,
  selectedStates,
  showNonSelectedTags,
  searchText,
  externalFocusedStandardId,
  onFocusedStandardsChange: _onFocusedStandardsChange,
  isDirty = false,
  onRevertChanges,
}: BipartiteChartProps) {
  const plotRef = useRef<any>(null);
  const [focusedStandardIndex, setFocusedStandardIndex] = useState<number | null>(null);

  // Use shared utility for cache keys and request building
  const allTagsKey = useMemo(
    () => createStandardsKey(tagCandidates, false),
    [tagCandidates]
  );

  const selectedTagsKey = useMemo(
    () => createStandardsKey(tagCandidates, true),
    [tagCandidates]
  );

  const selectedTags = useMemo(
    () => buildPreviewRequests(tagCandidates, mathExpressions, true),
    [tagCandidates, mathExpressions]
  );

  const allTags = useMemo(
    () => buildPreviewRequests(tagCandidates, mathExpressions, false),
    [tagCandidates, mathExpressions]
  );

  // Fetch standards for selected tags
  // Query key matches StandardsPreview for cache sharing
  const { data: standardsData } = useQuery({
    queryKey: ['standards-preview-selected', selectedTagsKey],
    queryFn: async () => {
      if (selectedTags.length === 0) {
        return { standards: [] as Standard[], tagToStandardsMap: new Map<string, string[]>() };
      }
      const results = await standardsApi.batchPreview(selectedTags);
      const tagToStandardsMap = new Map<string, string[]>();
      const uniqueStandards = new Map<string, Standard>();

      Object.entries(results).forEach(([key, standards]) => {
        const standardIds: string[] = [];
        standards.forEach((std) => {
          standardIds.push(std.standardId);
          if (!uniqueStandards.has(std.standardId)) {
            uniqueStandards.set(std.standardId, std);
          }
        });
        tagToStandardsMap.set(key, standardIds);
      });

      return {
        standards: Array.from(uniqueStandards.values()),
        tagToStandardsMap,
      };
    },
    enabled: selectedTags.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch standards for ALL tags (only when showNonSelectedTags is true)
  // Query key matches StandardsPreview for cache sharing
  const { data: allStandardsData } = useQuery({
    queryKey: ['standards-preview-all', allTagsKey],
    queryFn: async () => {
      if (allTags.length === 0) {
        return { standards: [] as Standard[], tagToStandardsMap: new Map<string, string[]>() };
      }
      const results = await standardsApi.batchPreview(allTags);
      const tagToStandardsMap = new Map<string, string[]>();
      const uniqueStandards = new Map<string, Standard>();

      Object.entries(results).forEach(([key, standards]) => {
        const standardIds: string[] = [];
        standards.forEach((std) => {
          standardIds.push(std.standardId);
          if (!uniqueStandards.has(std.standardId)) {
            uniqueStandards.set(std.standardId, std);
          }
        });
        tagToStandardsMap.set(key, standardIds);
      });

      return {
        standards: Array.from(uniqueStandards.values()),
        tagToStandardsMap,
      };
    },
    enabled: showNonSelectedTags && allTags.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Apply filters to standards data using shared filter function
  const filteredStandardsData = useMemo(() => {
    if (!standardsData || !standardsData.standards || standardsData.standards.length === 0) {
      return [];
    }

    const hasActiveFilters = selectedGrades.length > 0 ||
                             selectedDocuments.length > 0 ||
                             selectedJurisdictions.length > 0 ||
                             selectedStates.length > 0 ||
                             searchText.trim();

    let filtered = hasActiveFilters
      ? filterStandards(standardsData.standards, {
          grades: selectedGrades,
          documents: selectedDocuments,
          jurisdictions: selectedJurisdictions,
          states: selectedStates,
        })
      : standardsData.standards;

    // Apply search text filter
    if (searchText.trim()) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(standard =>
        standard.code?.toLowerCase().includes(search) ||
        standard.description?.toLowerCase().includes(search)
      );
    }

    return filtered;
  }, [standardsData, selectedGrades, selectedDocuments, selectedJurisdictions, selectedStates, searchText]);

  // Apply filters to ALL standards data (for unselected tags)
  const filteredAllStandardsData = useMemo(() => {
    if (!allStandardsData || !allStandardsData.standards || allStandardsData.standards.length === 0) {
      return [];
    }

    const hasActiveFilters = selectedGrades.length > 0 ||
                             selectedDocuments.length > 0 ||
                             selectedJurisdictions.length > 0 ||
                             selectedStates.length > 0 ||
                             searchText.trim();

    let filtered = hasActiveFilters
      ? filterStandards(allStandardsData.standards, {
          grades: selectedGrades,
          documents: selectedDocuments,
          jurisdictions: selectedJurisdictions,
          states: selectedStates,
        })
      : allStandardsData.standards;

    // Apply search text filter
    if (searchText.trim()) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(standard =>
        standard.code?.toLowerCase().includes(search) ||
        standard.description?.toLowerCase().includes(search)
      );
    }

    return filtered;
  }, [allStandardsData, selectedGrades, selectedDocuments, selectedJurisdictions, selectedStates, searchText]);

  // Track which standards are connected to selected tags (for coloring)
  const selectedTagStandardIds = useMemo(() => {
    const ids = new Set<string>();
    if (standardsData?.tagToStandardsMap) {
      standardsData.tagToStandardsMap.forEach((standardIds) => {
        standardIds.forEach((id) => ids.add(id));
      });
    }
    return ids;
  }, [standardsData]);

  // Calculate metrics for display
  const metrics = useMemo(() => {
    const totalTags = tagCandidates.length;
    const selectedCount = tagCandidates.filter(tag => tag?.competency?.selected).length;

    // Calculate total grades from all standards (unfiltered)
    const totalGrades = new Set<string>();
    if (standardsData && standardsData.standards && standardsData.standards.length > 0) {
      standardsData.standards.forEach((std) => {
        if (std.grade) totalGrades.add(std.grade);
      });
    }

    // Calculate visible grades from filtered standards
    const visibleGrades = new Set<string>();
    if (filteredStandardsData && filteredStandardsData.length > 0) {
      filteredStandardsData.forEach((std) => {
        if (std.grade) visibleGrades.add(std.grade);
      });
    }

    // Calculate standards counts
    const totalStandardsCount = standardsData?.standards?.length || 0;
    const visibleStandardsCount = filteredStandardsData?.length || 0;
    const allStandardsCount = allStandardsData?.standards?.length || 0;

    return {
      totalTags,
      selectedCount,
      totalGradesCount: totalGrades.size,
      visibleGradesCount: visibleGrades.size,
      totalGradesList: Array.from(totalGrades).sort().join(', ') || 'None',
      visibleGradesList: Array.from(visibleGrades).sort().join(', ') || 'None',
      totalStandardsCount,
      visibleStandardsCount,
      allStandardsCount,
    };
  }, [tagCandidates, standardsData, filteredStandardsData, allStandardsData]);

  // Process data for visualization
  const chartData = useMemo(() => {
    // Step 1: Collect tags to include with their original indices, then sort by description
    const tagsToInclude: { tag: TagCandidate; originalIndex: number }[] = [];

    tagCandidates.forEach((tag, index) => {
      if (!tag || !tag.competency) return;
      const isSelected = tag.competency.selected;
      const includeTag = isSelected || showNonSelectedTags;
      if (includeTag) {
        tagsToInclude.push({ tag, originalIndex: index });
      }
    });

    // Sort tags by competency.description ascending (A-Z from top to bottom)
    tagsToInclude.sort((a, b) => {
      const descA = a.tag.competency.description || '';
      const descB = b.tag.competency.description || '';
      return descA.localeCompare(descB);
    });

    // Build tag data structures from sorted list
    const tagLabels: string[] = [];
    const selectedTagIndices: number[] = [];
    const tagIndicesToInclude: number[] = [];
    const displayIndexToOriginalIndex: number[] = [];

    tagsToInclude.forEach(({ tag, originalIndex }, displayIndex) => {
      tagIndicesToInclude.push(originalIndex);
      displayIndexToOriginalIndex.push(originalIndex);

      const fullDesc = tag.competency.description || 'N/A';
      const label = fullDesc.includes('/') ? fullDesc.split('/').pop()!.trim() : fullDesc;
      tagLabels.push(label);

      if (tag.competency.selected) {
        selectedTagIndices.push(displayIndex);
      }
    });

    // Step 2: Collect all standards, dedupe, then sort by code
    const standardsMap = new Map<string, Standard>();

    // Add standards from selected tags (filtered)
    if (filteredStandardsData && filteredStandardsData.length > 0) {
      filteredStandardsData.forEach((standard: Standard) => {
        if (!standardsMap.has(standard.standardId)) {
          standardsMap.set(standard.standardId, standard);
        }
      });
    }

    // Add standards from unselected tags (if showNonSelectedTags is true)
    if (showNonSelectedTags && filteredAllStandardsData && filteredAllStandardsData.length > 0) {
      filteredAllStandardsData.forEach((standard: Standard) => {
        if (!standardsMap.has(standard.standardId)) {
          standardsMap.set(standard.standardId, standard);
        }
      });
    }

    // Sort standards by code ascending (A-Z from top to bottom)
    const sortedStandards = Array.from(standardsMap.values()).sort((a, b) => {
      const codeA = a.code || '';
      const codeB = b.code || '';
      return codeA.localeCompare(codeB);
    });

    // Track total count before truncation
    const totalStandardsBeforeLimit = sortedStandards.length;
    const standardsTruncated = totalStandardsBeforeLimit > MAX_STANDARD_NODES;

    // Limit standards to MAX_STANDARD_NODES for performance
    const limitedStandards = standardsTruncated
      ? sortedStandards.slice(0, MAX_STANDARD_NODES)
      : sortedStandards;

    // Build standard data structures from sorted (and possibly limited) list
    const standardLabels: string[] = [];
    const standardsDataArray: Standard[] = [];
    const standardIdToDisplayIndex = new Map<string, number>();

    limitedStandards.forEach((standard, displayIndex) => {
      const fullCode = standard.code || 'No Code';
      standardLabels.push(fullCode);
      standardsDataArray.push(standard);
      standardIdToDisplayIndex.set(standard.standardId, displayIndex);
    });

    // Step 3: Create connections using new display indices
    const connections: [number, number][] = [];

    // Create connections for selected tags
    if (standardsData && standardsData.tagToStandardsMap) {
      selectedTagIndices.forEach((displayTagIndex) => {
        const originalTagIndex = displayIndexToOriginalIndex[displayTagIndex];
        const tag = tagCandidates[originalTagIndex];

        if (tag && tag.competency) {
          const limiterIds = extractLimiterIds(tag);
          const mathExprIds = extractSelectedMathExpressionIds(tag);
          const mapKey = `${tag.competency.spine_node_id}:${limiterIds.sort().join(',')}:${mathExprIds.sort().join(',')}`;
          const relatedStandardIds = standardsData.tagToStandardsMap.get(mapKey) || [];

          relatedStandardIds.forEach((standardId: string) => {
            const standardDisplayIndex = standardIdToDisplayIndex.get(standardId);
            if (standardDisplayIndex !== undefined) {
              connections.push([displayTagIndex, standardDisplayIndex]);
            }
          });
        }
      });
    }

    // Create connections for unselected tags (if showNonSelectedTags is true)
    if (showNonSelectedTags && allStandardsData && allStandardsData.tagToStandardsMap) {
      tagIndicesToInclude.forEach((originalTagIndex, displayTagIndex) => {
        const tag = tagCandidates[originalTagIndex];
        // Skip selected tags (already handled above)
        if (!tag || !tag.competency || tag.competency.selected) {
          return;
        }

        const limiterIds = extractLimiterIds(tag);
        const mathExprIds = extractSelectedMathExpressionIds(tag);
        const mapKey = `${tag.competency.spine_node_id}:${limiterIds.sort().join(',')}:${mathExprIds.sort().join(',')}`;
        const relatedStandardIds = allStandardsData.tagToStandardsMap.get(mapKey) || [];

        relatedStandardIds.forEach((standardId: string) => {
          const standardDisplayIndex = standardIdToDisplayIndex.get(standardId);
          if (standardDisplayIndex !== undefined) {
            connections.push([displayTagIndex, standardDisplayIndex]);
          }
        });
      });
    }

    return {
      tagLabels,
      selectedTagIndices,
      standardLabels,
      standardsDataArray,
      connections,
      tagIndicesToInclude,
      selectedTagStandardIds, // Pass through for coloring
      standardsTruncated,
      totalStandardsBeforeLimit,
    };
  }, [tagCandidates, filteredStandardsData, filteredAllStandardsData, standardsData, allStandardsData, showNonSelectedTags, selectedTagStandardIds]);

  // Sync external standard focus with internal state
  useEffect(() => {
    if (!externalFocusedStandardId) {
      setFocusedStandardIndex(null);
      return;
    }

    // Find the display index for the external standard ID
    const sortedStandards = [...(filteredStandardsData || [])].sort((a, b) => {
      const codeA = a.code || '';
      const codeB = b.code || '';
      return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
    });

    const standardDisplayIndex = sortedStandards.findIndex(standard => standard.standardId === externalFocusedStandardId);

    if (standardDisplayIndex >= 0) {
      setFocusedStandardIndex(standardDisplayIndex);
    }
  }, [externalFocusedStandardId, filteredStandardsData]);

  // Helper function to get current focused tag standard IDs
  const getCurrentFocusedTagStandardIds = (): string[] => {
    if (focusedTagIndex === null || !standardsData?.tagToStandardsMap) {
      return [];
    }

    const originalTagIndex = chartData.tagIndicesToInclude[focusedTagIndex];
    const focusedTag = tagCandidates[originalTagIndex];

    if (!focusedTag) {
      return [];
    }

    // Use helper functions to extract limiter IDs and math expression IDs
    const selectedLimiters = extractLimiterIds(focusedTag);
    const mathExprIds = extractSelectedMathExpressionIds(focusedTag);

    const tagKey = `${focusedTag.competency.spine_node_id}:${selectedLimiters.sort().join(',')}:${mathExprIds.sort().join(',')}`;
    return standardsData.tagToStandardsMap.get(tagKey) || [];
  };

  // Build Plotly traces
  const traces = useMemo(() => {
    const { tagLabels, selectedTagIndices, standardLabels, standardsDataArray, connections, tagIndicesToInclude } = chartData;
    const traces: any[] = [];

    // Draw connection lines
    connections.forEach(([tagIdx, stdIdx]) => {
      const isSelected = selectedTagIndices.includes(tagIdx);
      // Map display index to original index for focus comparison
      const originalTagIndex = tagIndicesToInclude[tagIdx];
      const isTagFocused = originalTagIndex === focusedTagIndex;
      const isStandardFocused = stdIdx === focusedStandardIndex;

      // Determine line color based on focus state
      let lineColor = COLORS.UNSELECTED;
      if (isTagFocused) {
        lineColor = COLORS.FOCUSED; // Yellow for Tag -> Standard lookup
      } else if (isStandardFocused) {
        lineColor = COLORS.REVERSE_FOCUSED; // Green for Standard -> Tag lookup
      } else if (isSelected) {
        lineColor = COLORS.SELECTED; // Blue for selected but not focused
      }

      traces.push({
        x: [0, 1],
        y: [tagLabels.length - 1 - tagIdx, standardLabels.length - 1 - stdIdx],
        mode: 'lines',
        line: {
          color: lineColor,
          width: 1,
        },
        hoverinfo: 'skip',
        showlegend: false,
      });
    });

    // Find tags connected to focused standard (for green inner rings)
    const focusedStandardConnectedTags = new Set<number>();
    if (focusedStandardIndex !== null) {
      connections.forEach(([tagIdx, stdIdx]) => {
        if (stdIdx === focusedStandardIndex) {
          focusedStandardConnectedTags.add(tagIdx);
        }
      });
    }

    // Draw tag nodes
    const tagColors = tagLabels.map((_, idx) =>
      selectedTagIndices.includes(idx) ? COLORS.SELECTED : COLORS.UNSELECTED
    );
    const tagTextColors = tagLabels.map((_, idx) => {
      const originalTagIndex = tagIndicesToInclude[idx];
      return originalTagIndex === focusedTagIndex ? COLORS.FOCUSED : tagColors[idx];
    });
    const tagYPositions = tagLabels.map((_, idx) => tagLabels.length - 1 - idx);

    traces.push({
      x: Array(tagLabels.length).fill(0),
      y: tagYPositions,
      mode: 'markers+text',
      marker: {
        size: 15,
        color: tagColors,
        line: {
          color: tagLabels.map((_, idx) => {
            const originalTagIndex = tagIndicesToInclude[idx];
            return originalTagIndex === focusedTagIndex ? COLORS.FOCUSED : tagColors[idx];
          }),
          width: tagLabels.map((_, idx) => {
            const originalTagIndex = tagIndicesToInclude[idx];
            return originalTagIndex === focusedTagIndex ? 3 : 1;
          }),
        },
      },
      text: tagLabels,
      textposition: 'middle left',
      textfont: {
        color: tagTextColors,
        size: 12,
      },
      name: 'Tags',
      customdata: tagIndicesToInclude.map(idx => {
        const tag = tagCandidates[idx];
        const description = tag?.competency?.description || 'N/A';
        return styleTagTooltipText(description, 45); // Style hierarchical tag descriptions
      }), // Store styled descriptions for hover
      hovertemplate: '%{customdata}<extra></extra>',
    });

    // Draw inner circles for tags connected to focused standard
    if (focusedStandardConnectedTags.size > 0) {
      traces.push({
        x: Array(tagLabels.length).fill(0),
        y: tagYPositions,
        mode: 'markers',
        marker: {
          size: 8, // Smaller than main tag markers (which are size 15)
          color: tagLabels.map((_, idx) =>
            focusedStandardConnectedTags.has(idx) ? COLORS.REVERSE_FOCUSED : 'transparent'
          ),
          line: {
            width: 0, // No border for inner circles
          },
        },
        showlegend: false,
        hoverinfo: 'skip', // Don't interfere with main tag hover
      });
    }

    // Draw standard nodes (if any)
    if (standardLabels.length > 0) {
      const connectedStandardIndices = new Set(
        connections
          .filter(([tagIdx]) => selectedTagIndices.includes(tagIdx))
          .map(([, stdIdx]) => stdIdx)
      );

      // Get standards connected to the focused tag
      const focusedTagConnectedStandards = new Set(
        connections
          .filter(([tagIdx]) => {
            const originalTagIndex = tagIndicesToInclude[tagIdx];
            return originalTagIndex === focusedTagIndex;
          })
          .map(([, stdIdx]) => stdIdx)
      );

      // Determine base color for each standard (blue if connected to selected tag, grey otherwise)
      const standardBaseColors = standardLabels.map((_, idx) => {
        return connectedStandardIndices.has(idx) ? COLORS.SELECTED : COLORS.UNSELECTED;
      });

      const standardColors = standardLabels.map((_, idx) => {
        if (idx === focusedStandardIndex) {
          return COLORS.REVERSE_FOCUSED; // Green fill for focused standard
        }
        // Keep base color (blue/grey) for standards connected to focused tag - only border will be yellow
        return standardBaseColors[idx];
      });

      const standardTextColors = standardLabels.map((_, idx) => {
        if (idx === focusedStandardIndex) {
          return COLORS.REVERSE_FOCUSED; // Green text for focused standard
        }
        // Keep base color for text - only border highlights the focus relationship
        return standardBaseColors[idx];
      });
      const stdYPositions = standardLabels.map((_, idx) => standardLabels.length - 1 - idx);

      traces.push({
        x: Array(standardLabels.length).fill(1),
        y: stdYPositions,
        mode: 'markers+text',
        marker: {
          size: 15,
          color: standardColors,
          line: {
            color: standardLabels.map((_, idx) => {
              // Highlight border for standards connected to focused tag with yellow
              if (focusedTagConnectedStandards.has(idx)) {
                return COLORS.FOCUSED; // Yellow border for standards connected to focused tag
              }
              // Focused standard keeps its base border color (blue/grey)
              return standardBaseColors[idx];
            }),
            width: standardLabels.map((_, idx) => {
              // Thicker border for focused or connected-to-focused standards
              if (idx === focusedStandardIndex || focusedTagConnectedStandards.has(idx)) {
                return 3;
              }
              return 1;
            }),
          },
        },
        text: standardLabels,
        textposition: 'middle right',
        textfont: {
          color: standardTextColors,
          size: 10,
        },
        name: 'Standards',
        customdata: standardsDataArray.map((standard: Standard) =>
          styleStandardTooltipText(standard, 45) // Style complete standard info
        ),
        hovertemplate: '%{customdata}<extra></extra>',
        showlegend: false,
      });
    }

    return traces;
  }, [chartData, focusedTagIndex, focusedStandardIndex]);

  // Layout configuration
  const layout = {
    height: 650,
    dragmode: 'pan' as const, // Enable pan mode for dragging
    xaxis: {
      visible: false,
      range: [-0.5, 1.5],
      showgrid: false,
      zeroline: false,
      fixedrange: false, // Allow panning/zooming
    },
    yaxis: {
      visible: false,
      showgrid: false,
      zeroline: false,
      fixedrange: false, // Allow panning/zooming
    },
    showlegend: false,
    margin: { l: 0, r: 0, t: 0, b: 0 },
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
    hoverlabel: {
      bgcolor: '#ffffff',
      bordercolor: '#cccccc',
      font: {
        size: 12,
        color: '#333333',
      },
      namelength: -1,
      align: 'left' as const,
    },
    shapes: [
      {
        type: 'rect' as const,
        xref: 'paper' as const,
        yref: 'paper' as const,
        x0: 0,
        y0: 0,
        x1: 1,
        y1: 1,
        line: {
          color: COLORS.BORDER,
          width: 2,
        },
        fillcolor: 'rgba(0,0,0,0)',
      },
    ],
  };

  // Handle click events
  const handleClick = (event: any) => {
    if (event?.points && event.points.length > 0) {
      const point = event.points[0];
      // Check if it's a tag node (x=0)
      if (point.x === 0 && point.pointIndex !== undefined) {
        const clickedDisplayIndex = point.pointIndex;
        const clickedIndex = chartData.tagIndicesToInclude[clickedDisplayIndex];
        // Toggle tag focus (keep standard focus intact)
        if (focusedTagIndex === clickedIndex) {
          onTagFocus(null); // Unfocus if clicking same tag
          // Update standards callback - no focused tag
          _onFocusedStandardsChange?.({
            focusedStandardId: focusedStandardIndex !== null ? chartData.standardsDataArray[focusedStandardIndex]?.standardId || null : null,
            focusedTagStandardIds: []
          });
        } else {
          onTagFocus(clickedIndex);
          // Calculate focused tag standards for the newly focused tag
          const originalTagIndex = clickedIndex; // clickedIndex is the original tag index (43)
          const newFocusedTag = tagCandidates[originalTagIndex];
          let newFocusedTagStandardIds: string[] = [];


          if (newFocusedTag && standardsData?.tagToStandardsMap) {
            // Use helper functions to extract limiter IDs and math expression IDs
            const selectedLimiters = extractLimiterIds(newFocusedTag);
            const mathExprIds = extractSelectedMathExpressionIds(newFocusedTag);

            const tagKey = `${newFocusedTag.competency.spine_node_id}:${selectedLimiters.sort().join(',')}:${mathExprIds.sort().join(',')}`;
            newFocusedTagStandardIds = standardsData.tagToStandardsMap.get(tagKey) || [];
          }

          // Update standards callback with focused tag standards
          _onFocusedStandardsChange?.({
            focusedStandardId: focusedStandardIndex !== null ? chartData.standardsDataArray[focusedStandardIndex]?.standardId || null : null,
            focusedTagStandardIds: newFocusedTagStandardIds
          });
        }
      }
      // Check if it's a standard node (x=1)
      else if (point.x === 1 && point.pointIndex !== undefined) {
        const clickedStandardIndex = point.pointIndex;
        // Toggle standard focus (keep tag focus intact)
        if (focusedStandardIndex === clickedStandardIndex) {
          setFocusedStandardIndex(null); // Unfocus if clicking same standard
          // Call callback with no focused standard but keep focused tag standards
          const currentFocusedTagStandardIds = getCurrentFocusedTagStandardIds();
          _onFocusedStandardsChange?.({
            focusedStandardId: null,
            focusedTagStandardIds: currentFocusedTagStandardIds
          });
        } else {
          setFocusedStandardIndex(clickedStandardIndex);
          // Convert index to actual standard ID and call callback
          const focusedStandard = chartData.standardsDataArray[clickedStandardIndex];
          if (focusedStandard) {
            const currentFocusedTagStandardIds = getCurrentFocusedTagStandardIds();
            _onFocusedStandardsChange?.({
              focusedStandardId: focusedStandard.standardId,
              focusedTagStandardIds: currentFocusedTagStandardIds
            });
          }
        }
      }
    } else {
      // Click on empty space - unfocus both
      onTagFocus(null);
      setFocusedStandardIndex(null);
      // Call callback to unfocus all standards
      _onFocusedStandardsChange?.({
        focusedStandardId: null,
        focusedTagStandardIds: []
      });
    }
  };

  if (tagCandidates.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="textSecondary">
          No tag candidates available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header: 2-column layout - Left: title+metrics, Right: legend */}
      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
        {/* Column 1: Title and Metrics */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Tag-Standards Relationships
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap', pb: '10px' }}>
            <Typography variant="body2" component="span">
              Tags: <Box component="span" sx={{ color: 'primary.main' }}>{metrics.selectedCount}</Box>/<Box component="span" sx={{ color: COLORS.UNSELECTED }}>{metrics.totalTags}</Box>
              <Tooltip title={`${metrics.selectedCount} out of ${metrics.totalTags} possible competencies currently selected for these tags`} placement="top">
                <Box component="span" sx={{ ml: 0.5, cursor: 'help', color: 'text.secondary' }}>ⓘ</Box>
              </Tooltip>
            </Typography>

            <Typography variant="body2" component="span">
              Grades: {metrics.visibleGradesCount}/<Box component="span" sx={{ color: 'primary.main' }}>{metrics.totalGradesCount}</Box>
              <Tooltip
                title={
                  <Box>
                    <div>Total grades in aligned standards: [{metrics.totalGradesList}]</div>
                    <div>Grades in visible aligned standards: [{metrics.visibleGradesList}]</div>
                  </Box>
                }
                placement="top"
              >
                <Box component="span" sx={{ ml: 0.5, cursor: 'help', color: 'text.secondary' }}>ⓘ</Box>
              </Tooltip>
            </Typography>

            <Typography variant="body2" component="span">
              Standards: {metrics.visibleStandardsCount}/<Box component="span" sx={{ color: 'primary.main' }}>{metrics.totalStandardsCount}</Box>/<Box component="span" sx={{ color: COLORS.UNSELECTED }}>{metrics.allStandardsCount}</Box>
              <Tooltip
                title={
                  <Box>
                    <div>{metrics.visibleStandardsCount} visible (after filters)</div>
                    <div>{metrics.totalStandardsCount} from selected tags</div>
                    <div>{metrics.allStandardsCount} from all tags</div>
                  </Box>
                }
                placement="top"
              >
                <Box component="span" sx={{ ml: 0.5, cursor: 'help', color: 'text.secondary' }}>ⓘ</Box>
              </Tooltip>
            </Typography>

            {/* Compact Dirty State Alert */}
            {isDirty && (
              <Alert
                severity="warning"
                sx={{
                  py: 0,
                  px: 0.75,
                  fontSize: '0.75rem',
                  minHeight: 'auto',
                  border: 'none',
                  '& .MuiAlert-icon': { fontSize: '0.875rem', mt: 0, mr: 0.5 },
                  '& .MuiAlert-message': { display: 'flex', alignItems: 'center', py: 0 },
                }}
              >
                Unsaved changes
                {onRevertChanges && (
                  <Button size="small" onClick={onRevertChanges} sx={{ ml: 0.75, minHeight: 20, fontSize: '0.7rem', py: 0, px: 0.5 }}>
                    Revert
                  </Button>
                )}
              </Alert>
            )}
          </Box>
        </Box>

        {/* Column 2: Legend or Truncation Warning */}
        <Box
          sx={{
            bgcolor: chartData.standardsTruncated ? 'error.main' : 'background.paper',
            borderRadius: 1,
            px: 1.5,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 0.25,
            minWidth: 130,
            border: chartData.standardsTruncated ? 'none' : '1px solid',
            borderColor: 'divider',
          }}
        >
          {chartData.standardsTruncated ? (
            <>
              <Typography variant="caption" sx={{ color: 'error.contrastText', fontSize: '0.7rem', fontWeight: 600 }}>
                ⚠️ Showing {MAX_STANDARD_NODES}/{chartData.totalStandardsBeforeLimit} Standards
              </Typography>
              <Typography variant="caption" sx={{ color: 'error.contrastText', fontSize: '0.65rem' }}>
                Use more reasonable filters 👉
              </Typography>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: COLORS.SELECTED }} />
                <Typography variant="caption" sx={{ color: COLORS.SELECTED, fontSize: '0.65rem' }}>Aligned</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: COLORS.UNSELECTED }} />
                <Typography variant="caption" sx={{ color: COLORS.UNSELECTED, fontSize: '0.65rem' }}>Not Aligned</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: COLORS.FOCUSED }} />
                <Typography variant="caption" sx={{ color: COLORS.FOCUSED, fontSize: '0.65rem' }}>Focused Tag</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: COLORS.REVERSE_FOCUSED }} />
                <Typography variant="caption" sx={{ color: COLORS.REVERSE_FOCUSED, fontSize: '0.65rem' }}>Focused Standard</Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Chart container */}
      <Box sx={{ position: 'relative' }}>
        <StyledPlotWrapper>
          <Plot
            ref={plotRef}
            data={traces}
            layout={layout}
            config={{
              displayModeBar: false, // Hide modebar entirely
              scrollZoom: true, // Enable scroll wheel zoom
              doubleClick: 'reset' as const, // Double-click to reset axes
              responsive: true,
            }}
            onClick={handleClick}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler={true}
          />
        </StyledPlotWrapper>
      </Box>
    </Box>
  );
}
